import type { IAutocompleteKeywordGroup } from "../../AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import type {
    AiTopicSearchResult,
    AutocompleteDemandAnalysisItem,
    ISearchRelevance,
} from "../types/AutocompleteDemandAnalysisTypes";
import { AutocompleteDemandAnalysisEntityType, ENTITY_ICONS } from "../types/AutocompleteDemandAnalysisTypes";

import { getEntityLimit, AUTOCOMPLETE_LIMIT_MODE, type AutocompleteLimitMode } from "./consts";

const calculateSearchRelevance = (itemName: string, query: string): ISearchRelevance => {
    if (!query) {
        return {
            isExactMatch: false,
            startsWithQuery: false,
            matchIndex: -1,
        };
    }

    const lowerItemName = itemName.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const matchIndex = lowerItemName.indexOf(lowerQuery);

    return {
        isExactMatch: lowerItemName === lowerQuery,
        startsWithQuery: lowerItemName.startsWith(lowerQuery),
        matchIndex,
    };
};

const transformOwnList = (keywordGroup: IAutocompleteKeywordGroup, query: string): AutocompleteDemandAnalysisItem => ({
    id: `ownList_${keywordGroup.Id}`,
    name: keywordGroup.Name,
    type: AutocompleteDemandAnalysisEntityType.ownList,
    icon: ENTITY_ICONS[AutocompleteDemandAnalysisEntityType.ownList],
    searchRelevance: calculateSearchRelevance(keywordGroup.Name, query),
    originalData: keywordGroup,
});

const transformSharedList = (
    keywordGroup: IAutocompleteKeywordGroup,
    query: string,
): AutocompleteDemandAnalysisItem => ({
    id: `sharedList_${keywordGroup.Id}`,
    name: keywordGroup.Name,
    type: AutocompleteDemandAnalysisEntityType.sharedList,
    icon: ENTITY_ICONS[AutocompleteDemandAnalysisEntityType.sharedList],
    searchRelevance: calculateSearchRelevance(keywordGroup.Name, query),
    originalData: keywordGroup,
});

const transformAiTopic = (aiTopic: AiTopicSearchResult, query: string): AutocompleteDemandAnalysisItem => ({
    id: `aiTopic_${aiTopic.name}`,
    name: aiTopic.name,
    type: AutocompleteDemandAnalysisEntityType.aiTopic,
    icon: ENTITY_ICONS[AutocompleteDemandAnalysisEntityType.aiTopic],
    searchRelevance: calculateSearchRelevance(aiTopic.name, query),
    originalData: { seed: aiTopic.name },
});

/**
 * Sorts items within an entity group by relevance
 * Order: exact matches first, then starts-with matches, then contains matches
 */
const sortByRelevance = (items: AutocompleteDemandAnalysisItem[]): AutocompleteDemandAnalysisItem[] => {
    return items.toSorted((a, b) => {
        const aRelevance = a.searchRelevance;
        const bRelevance = b.searchRelevance;

        // Exact matches first
        if (aRelevance.isExactMatch && !bRelevance.isExactMatch) {
            return -1;
        }
        if (!aRelevance.isExactMatch && bRelevance.isExactMatch) {
            return 1;
        }

        // Then starts-with matches
        if (aRelevance.startsWithQuery && !bRelevance.startsWithQuery) {
            return -1;
        }
        if (!aRelevance.startsWithQuery && bRelevance.startsWithQuery) {
            return 1;
        }

        // Then by match position (earlier matches first)
        if (aRelevance.matchIndex !== bRelevance.matchIndex) {
            if (aRelevance.matchIndex === -1) {
                return 1;
            }
            if (bRelevance.matchIndex === -1) {
                return -1;
            }
            return aRelevance.matchIndex - bRelevance.matchIndex;
        }

        // Finally alphabetical
        return a.name.localeCompare(b.name);
    });
};

/**
 * Applies entity-specific limits and sorts by relevance (except for AI topics which are pre-sorted by backend)
 * When there's no query, preserves the original order
 */
const limitAndSortEntity = (
    items: AutocompleteDemandAnalysisItem[],
    entityType: AutocompleteDemandAnalysisEntityType,
    query: string,
    limitMode: AutocompleteLimitMode,
): AutocompleteDemandAnalysisItem[] => {
    const hasQuery = query.trim().length >= 2;

    // AI topics are already sorted by relevance by the backend
    if (entityType === AutocompleteDemandAnalysisEntityType.aiTopic) {
        const limit = getEntityLimit(entityType, limitMode);
        return items.slice(0, limit);
    }

    const processedItems = hasQuery ? sortByRelevance(items) : items;
    const limit = getEntityLimit(entityType, limitMode);
    return processedItems.slice(0, limit);
};

export const transformToUnifiedResults = (
    myKeywordGroups: IAutocompleteKeywordGroup[],
    sharedKeywordGroups: IAutocompleteKeywordGroup[],
    aiTopics: AiTopicSearchResult[],
    query: string,
    limitMode: AutocompleteLimitMode = AUTOCOMPLETE_LIMIT_MODE.unlimited,
): AutocompleteDemandAnalysisItem[] => {
    const ownListItems = myKeywordGroups.filter((group) => !group.SharedWithMe).map((group) => transformOwnList(group, query));

    const sharedListItems = sharedKeywordGroups
        .filter((group) => group.SharedWithMe)
        .map((group) => transformSharedList(group, query));

    const aiTopicItems = aiTopics.map((aiTopic) => transformAiTopic(aiTopic, query));

    const limitedOwnLists = limitAndSortEntity(
        ownListItems,
        AutocompleteDemandAnalysisEntityType.ownList,
        query,
        limitMode,
    );
    const limitedSharedLists = limitAndSortEntity(
        sharedListItems,
        AutocompleteDemandAnalysisEntityType.sharedList,
        query,
        limitMode,
    );
    const limitedAiTopics = limitAndSortEntity(
        aiTopicItems,
        AutocompleteDemandAnalysisEntityType.aiTopic,
        query,
        limitMode,
    );

    // Combined limit for all keyword groups: own lists fill first, shared lists take remaining slots
    const limit = getEntityLimit(AutocompleteDemandAnalysisEntityType.ownList, limitMode);
    const keywordGroups = [...limitedOwnLists, ...limitedSharedLists].slice(0, limit);

    // Combine in priority order: Own Lists -> Shared Lists -> AI Topics
    return [...keywordGroups, ...limitedAiTopics];
};
